import { useState } from 'react';
import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/constants';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import { ConstMap } from '@/shared/utils/const-map';
import { InitialPanelInput } from '../ui/discovery-prompt-input/initial-panel-input';
import { DiscoveryTemplateSuggestions } from '../ui/discovery-templates';
import { PlatformSelector } from '../ui/platform-selector';
import { useDiscoveryStore } from '../stores/discovery-store';
import type { DiscoverySession } from '@/entities/discovery';
import { useAtomValue } from 'jotai';
import { searchCreatorPendingAtom } from '../stores/mutation-atom';
import { useDiscoveryPromptSubmit } from '../hooks/use-discovery-prompt-submit';

export const DiscoveryInitialPanel = ({ session }: { session: DiscoverySession }) => {
  const { filters, setFilters, searchRequest } = useDiscoveryStore();
  const [inputValue, setInputValue] = useState(searchRequest.message);
  const searchPending = useAtomValue(searchCreatorPendingAtom);
  const { submitPrompt } = useDiscoveryPromptSubmit();

  const onPromptSubmit = async (prompt: string, source: 'free_text' | 'template' = 'free_text') => {
    await submitPrompt(session, prompt, filters, source);
  };

  const onPlatformChange = (platform: SOURCE_PLATFORM_TYPE) => {
    setFilters({
      ...filters,
      platform: [platform],
    });
  };

  const onFollowerChange = (followerRange: FOLLOWER_RANGE) => {
    const configs = followerRangeConfigs.get(followerRange);
    setFilters({
      ...filters,
      engagement: {
        ...filters.engagement,
        followers_max: configs.max,
        followers_min: configs.min,
      },
    });
  };

  return (
    <div className="flex w-full flex-col items-center gap-10">
      {/* Header Section */}
      <div className="flex flex-col items-center gap-3">
        <h1 className="text-primary text-3xl font-bold">Discover creators</h1>
        <p className="text-muted-foreground text-center text-sm">
          Describe the content style, filming setup, or creator characteristics you're looking for
        </p>

        {/* Platform & Follower Filter */}
        <div className="flex w-full items-center gap-3">
          <PlatformSelector
            values={filters.platform || []}
            onPlatformChange={onPlatformChange}
            isLoading={searchPending}
          />

          <Select
            value={followerRangeParser(
              filters.engagement?.followers_min || null,
              filters.engagement?.followers_max || null,
            )}
            onValueChange={(value: FOLLOWER_RANGE) => onFollowerChange(value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              {followerRangeConfigs.toArray().map((c) => {
                return (
                  <SelectItem key={c.key} value={c.key} disabled={c.value.disabled}>
                    {c.value.label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Input Section */}
      <div className="flex w-full max-w-190 flex-col items-center gap-6">
        <InitialPanelInput
          onSubmit={onPromptSubmit}
          disabled={searchPending}
          value={inputValue}
          onValueChange={setInputValue}
        />
        <DiscoveryTemplateSuggestions onSelect={onPromptSubmit} />
      </div>
    </div>
  );
};

enum FOLLOWER_RANGE {
  ALL = 'all',
  MICRO = 'micro',
  MINOR = 'minor',
  MAJOR = 'major',
  MEGA = 'mega',
}

const followerRangeConfigs = new ConstMap([
  [
    FOLLOWER_RANGE.ALL,
    {
      min: 10000,
      max: 100000,
      label: 'All followers',
    },
  ],
  [
    FOLLOWER_RANGE.MICRO,
    {
      min: 10000,
      max: 50000,
      label: '10K - 50K',
    },
  ],
  [
    FOLLOWER_RANGE.MINOR,
    {
      min: 50000,
      max: 100000,
      label: '50K - 100K',
    },
  ],
  [
    FOLLOWER_RANGE.MAJOR,
    {
      min: 100000,
      max: 500000,
      disabled: true,
      label: '100K - 500K',
    },
  ],
  [
    FOLLOWER_RANGE.MEGA,
    {
      min: 500000,
      max: null,
      disabled: true,
      label: '500K+',
    },
  ],
]);

const followerRangeParser = (min: number | null, max: number | null): FOLLOWER_RANGE => {
  const target = followerRangeConfigs
    .toArray()
    .find((c) => c.value.min === min && c.value.max === max);
  if (!target) return FOLLOWER_RANGE.ALL;
  return target.key;
};
